/* Patch 18.2 — RFQ Console + Conversion Wizard
   ================================================
   Lists Customer RFQs (from /api/customer-rfqs) with their conversion
   status and opens a 4-step Conversion Wizard mirroring Patch 17:

     Step 1: RFQ Review (rfq_no, customer, project, lines preview)
     Step 2: Customer Review + Dates (preselect RFQ.customer_id if set)
     Step 3: Items + Pricing (pre-filled from RFQ.lines, user adds unit_price)
     Step 4: Confirm + Conversion Notes → POST convert-to-quotation

   Also exposes a per-RFQ Timeline drawer (creation, audit entries,
   converted quotations) backed by GET /customer-rfqs/{id}/timeline.

   The page is a thin client of the existing Patch 18 backend; every
   action is server-validated (duplicate guard, tenant scope, role). */
import React, { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, FileText, ArrowRight, ArrowLeft, X, Plus, Trash2,
  CheckCircle2, Clock, AlertCircle, ListChecks,
} from 'lucide-react';

const fmt = (n) => (Number(n) || 0).toLocaleString('en-US',
  { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const today = () => new Date().toISOString().slice(0, 10);
const plus30 = () => {
  const d = new Date(); d.setDate(d.getDate() + 30);
  return d.toISOString().slice(0, 10);
};

export default function RfqConsole() {
  const [rfqs, setRfqs] = useState([]);
  const [statuses, setStatuses] = useState({}); // rfq_id → conversion_status
  const [loading, setLoading] = useState(false);
  const [wizardRfq, setWizardRfq] = useState(null);
  const [timelineRfq, setTimelineRfq] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axiosInstance.get('/customer-rfqs?limit=200');
      // Service returns {total, rfqs}. Older endpoints returned a flat
      // array — accept both shapes defensively.
      const items = Array.isArray(r.data)
        ? r.data
        : (r.data?.rfqs || r.data?.items || []);
      setRfqs(items);
      // Lazy-load conversion-status per RFQ (batched in parallel).
      const next = {};
      await Promise.all(items.map(async (rf) => {
        try {
          const s = await axiosInstance.get(`/customer-rfqs/${rf.id}/conversion-status`);
          next[rf.id] = s.data;
        } catch { /* tenant/role may 403 — keep state out of map */ }
      }));
      setStatuses(next);
    } catch (e) {
      toast.error(`Load failed: ${e.response?.data?.detail || e.message}`);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const counts = rfqs.reduce((acc, rf) => {
    const st = statuses[rf.id]?.state || 'none';
    acc[st] = (acc[st] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-5" data-testid="rfq-console-page">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 inline-flex items-center gap-2">
            <ListChecks className="text-indigo-600" size={26} /> RFQ → Quotation
          </h1>
          <p className="text-sm text-slate-600">
            Convert Customer RFQs into priced quotations with full source
            traceability — the backend guards duplicates and tenant scope.
          </p>
        </div>
        <button
          data-testid="rfq-refresh-btn"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white rounded px-4 py-1.5 text-sm font-semibold disabled:opacity-50"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Refresh
        </button>
      </header>

      <div className="grid grid-cols-4 gap-3" data-testid="rfq-summary-cards">
        <SummaryCard testid="rfq-stat-total"        label="Total RFQs"            value={rfqs.length}              cls="text-slate-900" />
        <SummaryCard testid="rfq-stat-not-converted" label="Not Converted"         value={counts.none || 0}         cls="text-amber-700" />
        <SummaryCard testid="rfq-stat-converted"    label="Converted"             value={counts.converted || 0}    cls="text-emerald-700" />
        <SummaryCard testid="rfq-stat-alternatives" label="Alternative Quotations" value={counts.alternative || 0} cls="text-indigo-700" />
      </div>

      <section className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-xs" data-testid="rfq-table">
          <thead>
            <tr className="bg-slate-50 text-left text-[10px] uppercase text-slate-500">
              <th className="px-3 py-2">RFQ #</th>
              <th className="px-3 py-2">Customer / Project</th>
              <th className="px-3 py-2">Lines</th>
              <th className="px-3 py-2">Created</th>
              <th className="px-3 py-2">Conversion</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rfqs.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-8 text-slate-500" data-testid="rfq-empty-state">No customer RFQs yet.</td></tr>
            ) : (
              rfqs.map((rf, i) => {
                const s = statuses[rf.id];
                const state = s?.state || 'none';
                const badgeCls = {
                  none:        'bg-amber-100 text-amber-800',
                  converted:   'bg-emerald-100 text-emerald-800',
                  alternative: 'bg-indigo-100 text-indigo-800',
                }[state] || 'bg-slate-100';
                return (
                  <tr key={rf.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`rfq-row-${i}`}>
                    <td className="px-3 py-2 font-mono font-semibold text-slate-800">{rf.rfq_no}</td>
                    <td className="px-3 py-2">
                      <div className="font-semibold text-slate-900">{rf.customer_name || '—'}</div>
                      <div className="text-[10px] text-slate-500">{rf.project_name || '—'}</div>
                    </td>
                    <td className="px-3 py-2 text-slate-700">{(rf.lines || []).length}</td>
                    <td className="px-3 py-2 text-[10px] text-slate-500">{(rf.created_at || '').slice(0, 10)}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${badgeCls}`} data-testid={`rfq-state-${i}`}>
                        {state.toUpperCase()}
                      </span>
                      {s?.quotation_count > 0 && (
                        <div className="text-[10px] text-slate-500 mt-0.5">
                          {s.quotations.map(q => q.quotation_no).join(', ')}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button
                        data-testid={`rfq-convert-${i}`}
                        onClick={() => setWizardRfq(rf)}
                        className="inline-flex items-center gap-1 bg-indigo-600 hover:bg-indigo-700 text-white text-[10px] font-semibold rounded px-2 py-1 mr-1"
                      >
                        {state === 'none' ? 'Convert' : 'Alternative'} <ArrowRight size={9} />
                      </button>
                      <button
                        data-testid={`rfq-timeline-${i}`}
                        onClick={() => setTimelineRfq(rf)}
                        className="inline-flex items-center gap-1 bg-slate-200 hover:bg-slate-300 text-slate-700 text-[10px] font-semibold rounded px-2 py-1"
                      >
                        <Clock size={9} /> Timeline
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </section>

      {wizardRfq && (
        <ConversionWizard
          rfq={wizardRfq}
          isAlternative={(statuses[wizardRfq.id]?.state || 'none') !== 'none'}
          onClose={() => setWizardRfq(null)}
          onSuccess={async () => { setWizardRfq(null); await load(); }}
        />
      )}

      {timelineRfq && (
        <TimelineDrawer
          rfq={timelineRfq}
          onClose={() => setTimelineRfq(null)}
        />
      )}
    </div>
  );
}


function SummaryCard({ testid, label, value, cls }) {
  return (
    <div className="bg-white rounded-lg p-3 border border-slate-200" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-3xl font-bold font-mono mt-1 ${cls}`}>{value}</div>
    </div>
  );
}


function ConversionWizard({ rfq, isAlternative, onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState(rfq.customer_id || '');
  // Pre-fill items from RFQ lines. RFQ lines already carry description /
  // qty / unit / size — the wizard adds unit_price (required by the
  // backend convert endpoint).
  const [items, setItems] = useState(
    (rfq.lines || []).length > 0
      ? rfq.lines.map((ln) => ({
          description: [ln.description, ln.size].filter(Boolean).join(' — '),
          quantity: Number(ln.qty) || 1,
          unit: ln.unit || 'ls',
          unit_price: 0,
          total: 0,
        }))
      : [{ description: '', quantity: 1, unit_price: 0, unit: 'ls', total: 0 }]
  );
  const [quotationDate, setQuotationDate] = useState(today());
  const [validDate, setValidDate] = useState(plus30());
  const [vatRate, setVatRate] = useState(15);
  const [notes, setNotes] = useState('');
  const [conversionNotes, setConversionNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axiosInstance.get('/customers').then(r => setCustomers(r.data || []))
      .catch(() => toast.error('Failed to load customers'));
  }, []);

  const subtotal = items.reduce(
    (s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);
  const vatAmt = +(subtotal * vatRate / 100).toFixed(2);
  const total = +(subtotal + vatAmt).toFixed(2);

  const updateItem = (idx, patch) => {
    setItems(items.map((it, i) => i === idx
      ? { ...it, ...patch,
          total: ((patch.quantity ?? it.quantity) * (patch.unit_price ?? it.unit_price)) || 0 }
      : it));
  };
  const addItem = () => setItems([...items,
    { description: '', quantity: 1, unit_price: 0, unit: 'ls', total: 0 }]);
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await axiosInstance.post(
        `/customer-rfqs/${rfq.id}/convert-to-quotation`,
        {
          customer_id: customerId,
          items,
          quotation_date: quotationDate,
          valid_date: validDate,
          vat_rate: vatRate,
          notes,
          conversion_notes: conversionNotes,
          force_alternative: isAlternative,
        });
      toast.success(
        `Created ${r.data.quotation_no} ✓${r.data.is_alternative ? ' (alternative)' : ''}`);
      await onSuccess();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(`Convert failed: ${typeof d === 'object' ? (d.error || JSON.stringify(d)) : (d || e.message)}`);
    } finally { setSubmitting(false); }
  };

  const stepValid = {
    1: true,
    2: !!customerId && !!quotationDate && !!validDate,
    3: items.length > 0 && items.every(it => it.description && it.quantity > 0 && it.unit_price >= 0),
    4: true,
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" data-testid="rfq-conversion-wizard">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[92vh] overflow-y-auto">
        <header className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase text-slate-500">
              {isAlternative ? 'Create Alternative Quotation' : 'Convert RFQ to Quotation'}
            </div>
            <h2 className="text-base font-bold text-slate-900">
              {rfq.rfq_no} — {rfq.customer_name || 'No customer'}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <StepDots step={step} />
            <button data-testid="rfq-wizard-close" onClick={onClose} className="p-2 hover:bg-slate-100 rounded">
              <X size={16} />
            </button>
          </div>
        </header>

        <div className="p-5">
          {step === 1 && (
            <div data-testid="rfq-wizard-step-1" className="space-y-3">
              <h3 className="text-sm font-bold">Step 1 of 4 — RFQ Review</h3>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <Field label="RFQ #"          value={rfq.rfq_no} />
                <Field label="Status"         value={rfq.status} />
                <Field label="Customer"       value={rfq.customer_name || '—'} />
                <Field label="Project"        value={rfq.project_name || '—'} />
                <Field label="Contact Person" value={rfq.contact_person || '—'} />
                <Field label="Lines"          value={(rfq.lines || []).length} />
              </div>
              {rfq.notes && (
                <div className="bg-indigo-50 border border-indigo-200 rounded p-2 text-xs">
                  <strong>Notes:</strong> {rfq.notes}
                </div>
              )}
              {(rfq.lines || []).length > 0 && (
                <div className="border border-slate-200 rounded">
                  <table className="w-full text-[11px]">
                    <thead className="bg-slate-50">
                      <tr className="text-left text-[10px] uppercase text-slate-500">
                        <th className="px-2 py-1">#</th>
                        <th className="px-2 py-1">Description</th>
                        <th className="px-2 py-1 w-16">Qty</th>
                        <th className="px-2 py-1 w-12">Unit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rfq.lines.map((ln, i) => (
                        <tr key={i} className="border-t border-slate-100" data-testid={`rfq-line-preview-${i}`}>
                          <td className="px-2 py-1 text-slate-500">{ln.no || i + 1}</td>
                          <td className="px-2 py-1">{ln.description || '—'} {ln.size ? `(${ln.size})` : ''}</td>
                          <td className="px-2 py-1 font-mono">{ln.qty}</td>
                          <td className="px-2 py-1">{ln.unit || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {isAlternative && (
                <div className="bg-amber-50 border border-amber-300 rounded p-2 text-xs text-amber-900 inline-flex items-center gap-1">
                  <AlertCircle size={12} /> This RFQ already has a converted quotation.
                  You are creating an alternative — for revisions use the quotation&apos;s Revise action.
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div data-testid="rfq-wizard-step-2" className="space-y-3">
              <h3 className="text-sm font-bold">Step 2 of 4 — Customer & Dates</h3>
              <p className="text-xs text-slate-600">
                {rfq.customer_id
                  ? 'Customer preselected from RFQ. Override only if needed.'
                  : 'This RFQ has no linked customer — pick one for the quotation.'}
              </p>
              <select
                data-testid="rfq-wizard-customer-select"
                value={customerId}
                onChange={e => setCustomerId(e.target.value)}
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
              >
                <option value="">— Select customer —</option>
                {customers.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name_english || c.name_arabic} — {c.customer_number || ''}
                  </option>
                ))}
              </select>
              {!customerId && (
                <div
                  data-testid="rfq-wizard-customer-required-hint"
                  className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-flex items-center gap-1"
                >
                  <AlertCircle size={11} /> Select a customer to continue.
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <Field label="Quotation Date">
                  <input type="date" value={quotationDate} onChange={e => setQuotationDate(e.target.value)}
                    data-testid="rfq-wizard-quotation-date"
                    className="border border-slate-300 rounded px-2 py-1 text-xs w-full" />
                </Field>
                <Field label="Valid Until">
                  <input type="date" value={validDate} onChange={e => setValidDate(e.target.value)}
                    data-testid="rfq-wizard-valid-date"
                    className="border border-slate-300 rounded px-2 py-1 text-xs w-full" />
                </Field>
              </div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="rfq-wizard-step-3" className="space-y-3">
              <h3 className="text-sm font-bold">Step 3 of 4 — Items & Pricing</h3>
              <p className="text-xs text-slate-600">
                Lines were pre-filled from the RFQ. Add unit prices to each item — totals update live.
              </p>
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-50 text-left text-[10px] uppercase text-slate-500">
                    <th className="px-2 py-1">Description</th>
                    <th className="px-2 py-1 w-16">Qty</th>
                    <th className="px-2 py-1 w-12">Unit</th>
                    <th className="px-2 py-1 w-24">Unit Price</th>
                    <th className="px-2 py-1 w-24 text-right">Total</th>
                    <th className="px-2 py-1 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, idx) => (
                    <tr key={idx} className="border-t border-slate-100" data-testid={`rfq-wizard-item-${idx}`}>
                      <td className="px-2 py-1">
                        <input value={it.description} onChange={e => updateItem(idx, { description: e.target.value })}
                          placeholder="Item description"
                          className="border border-slate-300 rounded px-1.5 py-0.5 text-xs w-full" />
                      </td>
                      <td className="px-2 py-1">
                        <input type="number" value={it.quantity} onChange={e => updateItem(idx, { quantity: +e.target.value })}
                          className="border border-slate-300 rounded px-1.5 py-0.5 text-xs w-full" />
                      </td>
                      <td className="px-2 py-1">
                        <input value={it.unit} onChange={e => updateItem(idx, { unit: e.target.value })}
                          className="border border-slate-300 rounded px-1.5 py-0.5 text-xs w-full" />
                      </td>
                      <td className="px-2 py-1">
                        <input type="number" value={it.unit_price} onChange={e => updateItem(idx, { unit_price: +e.target.value })}
                          data-testid={`rfq-wizard-unit-price-${idx}`}
                          className="border border-slate-300 rounded px-1.5 py-0.5 text-xs w-full" />
                      </td>
                      <td className="px-2 py-1 text-right font-mono">{fmt(it.total)}</td>
                      <td className="px-2 py-1">
                        {items.length > 1 && (
                          <button onClick={() => removeItem(idx)} className="text-rose-500 hover:text-rose-700"
                            data-testid={`rfq-wizard-remove-item-${idx}`}>
                            <Trash2 size={11} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button onClick={addItem} data-testid="rfq-wizard-add-item"
                className="text-xs text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1">
                <Plus size={11} /> Add item
              </button>
              <div className="grid grid-cols-3 gap-3 mt-3 bg-slate-50 rounded p-3 text-xs">
                <Field label="Subtotal" value={fmt(subtotal)} mono />
                <Field label="VAT (%)">
                  <input type="number" value={vatRate} onChange={e => setVatRate(+e.target.value)}
                    data-testid="rfq-wizard-vat-rate"
                    className="border border-slate-300 rounded px-2 py-1 text-xs w-full" />
                </Field>
                <Field label="Total" value={fmt(total)} mono cls="font-bold text-slate-900" />
              </div>
            </div>
          )}

          {step === 4 && (
            <div data-testid="rfq-wizard-step-4" className="space-y-3">
              <h3 className="text-sm font-bold">Step 4 of 4 — Confirm</h3>
              <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-xs">
                <div><strong>Customer:</strong> {(() => {
                  const c = customers.find(c => c.id === customerId);
                  return c ? (c.name_english || c.name_arabic || customerId) : customerId;
                })()}</div>
                <div><strong>Items:</strong> {items.length} · <strong>Total:</strong> {fmt(total)} SAR</div>
                <div><strong>Source:</strong> RFQ {rfq.rfq_no}</div>
                {isAlternative && (
                  <div className="text-amber-800 font-semibold mt-1">⚠ Alternative quotation — backend will create a parallel quote.</div>
                )}
              </div>
              <Field label="Conversion Notes (optional)">
                <textarea value={conversionNotes} onChange={e => setConversionNotes(e.target.value)}
                  data-testid="rfq-wizard-conversion-notes"
                  rows={2} placeholder="Why this conversion now? Surfaces in the RFQ timeline & quotation narrative."
                  className="w-full border border-slate-300 rounded px-2 py-1.5 text-xs" />
              </Field>
              <Field label="Quotation Notes (optional)">
                <textarea value={notes} onChange={e => setNotes(e.target.value)}
                  rows={2} className="w-full border border-slate-300 rounded px-2 py-1.5 text-xs" />
              </Field>
            </div>
          )}
        </div>

        <footer className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          {step > 1 && (
            <button onClick={() => setStep(step - 1)}
              data-testid="rfq-wizard-back-btn"
              className="px-3 py-1.5 text-xs rounded border border-slate-300 inline-flex items-center gap-1">
              <ArrowLeft size={11} /> Back
            </button>
          )}
          {step < 4 ? (
            <button
              data-testid="rfq-wizard-next-btn"
              disabled={!stepValid[step]}
              onClick={() => setStep(step + 1)}
              className="px-3 py-1.5 text-xs rounded bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white font-semibold inline-flex items-center gap-1"
            >
              Next <ArrowRight size={11} />
            </button>
          ) : (
            <button
              data-testid="rfq-wizard-create-btn"
              disabled={submitting}
              onClick={submit}
              className="px-3 py-1.5 text-xs rounded bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white font-semibold inline-flex items-center gap-1"
            >
              {submitting ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />}
              {isAlternative ? 'Create Alternative' : 'Create Quotation'}
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}


function StepDots({ step }) {
  return (
    <div className="flex items-center gap-1" data-testid="rfq-wizard-step-dots">
      {[1, 2, 3, 4].map(n => (
        <div key={n} className={`w-2 h-2 rounded-full ${
          n < step ? 'bg-emerald-500' : n === step ? 'bg-slate-900' : 'bg-slate-300'
        }`} />
      ))}
    </div>
  );
}


function Field({ label, value, children, mono, cls }) {
  return (
    <div>
      <div className="text-[10px] uppercase text-slate-500">{label}</div>
      {children
        ? <div className="mt-0.5">{children}</div>
        : <div className={`text-xs text-slate-900 ${mono ? 'font-mono' : ''} ${cls || ''}`}>{value || '—'}</div>}
    </div>
  );
}


function TimelineDrawer({ rfq, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axiosInstance.get(`/customer-rfqs/${rfq.id}/timeline`)
      .then(r => setData(r.data))
      .catch(e => toast.error(`Timeline failed: ${e.response?.data?.detail?.error || e.message}`))
      .finally(() => setLoading(false));
  }, [rfq.id]);

  const TYPE_META = {
    rfq_created:                   { icon: ListChecks, cls: 'text-indigo-600' },
    converted_to_quotation:        { icon: ArrowRight, cls: 'text-emerald-600' },
    alternative_quotation_created: { icon: ArrowRight, cls: 'text-indigo-700' },
    quotation_created:             { icon: FileText, cls: 'text-slate-600' },
    quotation_approved:            { icon: CheckCircle2, cls: 'text-emerald-600' },
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="rfq-timeline-drawer">
      <button className="flex-1 bg-slate-900/40" onClick={onClose} aria-label="Close timeline" />
      <div className="w-full max-w-md bg-white shadow-2xl flex flex-col">
        <header className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase text-slate-500">RFQ Timeline</div>
            <h2 className="text-base font-bold">{rfq.rfq_no}</h2>
          </div>
          <button data-testid="rfq-timeline-close" onClick={onClose} className="p-2 hover:bg-slate-100 rounded">
            <X size={16} />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto p-5">
          {loading && <Loader2 size={14} className="animate-spin text-slate-500" data-testid="rfq-timeline-loading" />}
          {data && (
            <ol className="relative border-l-2 border-slate-200 ml-2" data-testid="rfq-timeline-events">
              {(data.events || []).map((e, i) => {
                const tm = TYPE_META[e.type] || { icon: Clock, cls: 'text-slate-400' };
                const Icon = tm.icon;
                return (
                  <li key={i} className="mb-4 ml-4" data-testid={`rfq-timeline-event-${i}`}>
                    <span className="absolute -left-[9px] flex items-center justify-center w-4 h-4 rounded-full bg-white border border-slate-300">
                      <Icon size={9} className={tm.cls} />
                    </span>
                    <div className="text-[10px] text-slate-500">{(e.ts || '').slice(0, 19).replace('T', ' ')}</div>
                    <div className="text-sm font-bold text-slate-900">{e.label}</div>
                    {e.summary && <div className="text-xs text-slate-600 mt-0.5">{e.summary}</div>}
                    {e.actor && <div className="text-[10px] text-slate-400 mt-0.5">by {e.actor}</div>}
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}
